import DOMPurify from 'isomorphic-dompurify';
import normalizeUrl from './normalizeUrl';
import type { LinkType } from '#shared/types/LinkType';
import type { FormErrors, PricingTier } from '#shared/types/miscellaneous';

const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

const capitalizeWords = (string: string) => {
    const newString = string.split(' ');
    for (let i = 0, x = newString.length; i < x; i++) {
        newString[i] = newString[i][0].toUpperCase() + newString[i].substr(1);
    }
    return newString.join(' ');
};

const calculatePrice = (quantity: number, pricingTiers: PricingTier[]) => {
    let pricePerUnit = 0;

    pricingTiers.forEach((pricingTier) => {
        if (pricingTier.quantityBreak <= quantity) {
            pricePerUnit = pricingTier.price;
        }
    });

    return quantity * pricePerUnit;
};

const commaNumber = (value: number) => value.toLocaleString('en');

const formatCurrency = (value: number, digits = 2) => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

    return formatter.format(value);
};

const formatDecimals = (value: number, decimals: number) => {
    return value.toFixed(decimals);
};

const formatPhone = (phone: string) => {
    if (phone.length === 10) {
        return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6, 10)}`;
    }

    return phone;
};

const getDecimalPlaces = (number: number) => {
    const numberAsString = number.toString();

    if (!numberAsString.includes('.')) {
        return 0;
    }

    const parts = numberAsString.split('.');
    return parts[1].length;
};

const getFormErrors = (responseData: any) => {
    const formErrors: FormErrors = {};

    if (responseData.message) {
        formErrors.form = responseData.message;
    }

    if (responseData.status === 'fail' && responseData.data) {
        Object.assign(formErrors, responseData.data);
    }

    return formErrors;
};

const getResponseErrorsAsHtml = (responseData: any) => {
    if (!responseData) {
        return null;
    }

    if (
        responseData.status === 'fail' &&
        responseData.data &&
        typeof responseData.data === 'object'
    ) {
        const errors: string[] = [];
        Object.values(responseData.data).forEach((error) => {
            if (Array.isArray(error)) {
                errors.push(...error);
            } else if ('string' === typeof error) {
                errors.push(error);
            }
        });
        return errors.join('<br>');
    }

    if (responseData.message) {
        return responseData.message;
    }

    return null;
};

const roundToNearestIncrement = (value: number, increment: number) => {
    const correctionFactor = 10 ** getDecimalPlaces(increment);

    const correctedValue = correctionFactor * value;
    const correctedIncrement = correctionFactor * increment;

    const result =
        Math.round(correctedValue / correctedIncrement) * correctedIncrement;

    return result / correctionFactor;
};

const sanitizeHtml = (unsafeHtml: string): string => {
    return DOMPurify.sanitize(unsafeHtml, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target'],
    });
};

const getProductDisplayName = (productName: string, industry?: string) => {
    let displayName = productName;
    if (industry === undefined) {
        displayName = displayName.replace('[industry]', '');
        return displayName;
    }
    if (displayName.includes('[industry]')) {
        displayName = displayName.replace(
            '[industry]',
            capitalizeFirstLetter(industry),
        );
        return displayName;
    }

    const validProducts = ['bag', 'pouch', 'can'];
    const containsText = (text: string, array: string[]) => {
        return array.some((value) => text.includes(value));
    };

    const lastWord = displayName.substring(displayName.lastIndexOf(' ') + 1);
    const hasValidProduct = containsText(lastWord.toLowerCase(), validProducts);
    if (hasValidProduct) {
        const pos = displayName.length - lastWord.length;
        displayName = `${displayName.slice(0, pos)}${capitalizeFirstLetter(
            industry,
        )} ${displayName.slice(pos)}`;
        return displayName;
    }

    return displayName;
};

const scrollToId = (id: string) => {
    const node = document.getElementById(id);
    if (node) {
        node.scrollIntoView({ behavior: 'smooth' });
    }
};

const scrollToTop = (smooth = true) => {
    window.scrollTo({
        top: 0,
        behavior: smooth ? 'smooth' : 'instant',
    });
};

const linkFromStoryblokLinkBlock = (link: any): LinkType => {
    const formatted = {
        url: '#',
        label: '',
    };

    if (link?.label && link?.target) {
        const url = urlFromStoryblokLinkField(link.target);
        if (url) {
            formatted.label = link.label;
            formatted.url = url;
        }
    }

    return formatted;
};

const urlFromStoryblokLinkField = (link: any): string => {
    if (!link) {
        return '';
    }

    if (link.story?.url) {
        return normalizeUrl(link.story.url);
    }

    if (link.url) {
        return normalizeUrl(link.url);
    }

    if (link.cached_url) {
        return normalizeUrl(link.cached_url);
    }

    if (link.story?.full_slug) {
        return normalizeUrl(link.story.full_slug);
    }

    return '';
};

const slugify = (str: string): string => {
    str = str.replace(/^\s+|\s+$/g, '');
    str = str.toLowerCase();
    str = str
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    return str;
};

const deslugify = (str: string): string => {
    str = str.replace(/[-_]/g, ' ');
    return capitalizeWords(str);
};

const formattedDate = (date: string): string => {
    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    const thisDate = new Date(date);
    const day = thisDate.getDate();
    const month = thisDate.getMonth();
    const year = thisDate.getFullYear();
    return `${months[month]} ${day}, ${year}`;
};

const currentYear = (): number => {
    return new Date().getFullYear();
};

const compare = (
    a: string | number,
    b: string | number,
    descending = false,
) => {
    if (a < b) {
        return descending ? 1 : -1;
    }
    if (a > b) {
        return descending ? -1 : 1;
    }
    return 0;
};

const isValidHttpUrl = (urlValue: string) => {
    try {
        const url = new URL(urlValue);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (err) {
        return false;
    }
};

const pluralizeWord = (word: string): string => {
    if (!word) return word;
    if (/(?:ss|x|z|ch|sh)$/i.test(word)) return `${word}es`;
    if (/s$/i.test(word)) return word;
    if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
    return `${word}s`;
};

const pluralize = (num: number, word: string, plural = pluralizeWord(word)) =>
    [1, -1].includes(Number(num)) ? word : plural;

/**
 * Lowercases a title-cased word ("Pouch" -> "pouch", "Stand-Up" -> "stand-up")
 * while leaving acronyms and brand casing alone ("PCR", "K-Cup", "Fill-N-Ship").
 */
const lowercaseTitleCaseWord = (word: string): string =>
    word.split('-').every((part) => /^[A-Z]?[a-z]+$/.test(part))
        ? word.toLowerCase()
        : word;

const pluralizeProductName = (title: string | null | undefined): string => {
    const words = (title ?? '').trim().split(/\s+/).filter(Boolean);
    const last = words.at(-1);
    if (!last) return 'products';

    words[words.length - 1] = pluralizeWord(last);
    return words.map(lowercaseTitleCaseWord).join(' ');
};

const normalizeUrlWithinAnchors = (str: string) => {
    return str.replaceAll(/(href=")(.*?)(")/g, (match, prefix, url, suffix) => {
        return prefix + normalizeUrl(url) + suffix;
    });
};

const titleCase = (s: string) =>
    s
        .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
        .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase());

const slugToSpaceSeparatedWords = (value: string) =>
    decodeURIComponent(value)
        .replace(/[-_]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');

const humanizeSlug = (slug: string) =>
    slugToSpaceSeparatedWords(slug).replace(/\b\w/g, (char) =>
        char.toUpperCase(),
    );

const normalizeMaterialValue = (material: string | null | undefined): string =>
    slugToSpaceSeparatedWords(material ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export {
    calculatePrice,
    capitalizeFirstLetter,
    capitalizeWords,
    commaNumber,
    compare,
    currentYear,
    deslugify,
    formatCurrency,
    formatDecimals,
    formatPhone,
    formattedDate,
    getDecimalPlaces,
    getFormErrors,
    getProductDisplayName,
    getResponseErrorsAsHtml,
    humanizeSlug,
    isValidHttpUrl,
    linkFromStoryblokLinkBlock,
    normalizeMaterialValue,
    normalizeUrlWithinAnchors,
    pluralize,
    pluralizeProductName,
    pluralizeWord,
    roundToNearestIncrement,
    sanitizeHtml,
    scrollToId,
    scrollToTop,
    slugify,
    titleCase,
    urlFromStoryblokLinkField,
};
